<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
    :error="control.control.value.errors"
  >
    <section class="flex w-full flex-col items-start">
      <slot name="header">
        <div class="w-full" :class="control.styles.control.label">
          <div class="flex items-center justify-between">
            <h3>{{ control.computedLabel.value }}</h3>
            <div v-if="control.control.value?.data?.latitude" class="flex items-center gap-2">
              <WButton type="ghost" :config="getClientTheme()" @click="toggleMap(true)">
                <WidgetIcon :icon="controlOptions.filled.edit" height="1.5rem" class="widget-action-icon" />
              </WButton>
              <WButton type="ghost" :config="getClientTheme()" @click="isOpenDeleteModal = true">
                <WidgetIcon :icon="controlOptions.filled.delete" height="1.5rem" class="widget-action-icon-negative" />
              </WButton>
            </div>
          </div>
          <p
            class="body1 font-normal"
            :class="[
              checkTheme('bmw', 'toyota')
                ? '!normal-case text-fontPrimary-500'
                : checkTheme('jlr')
                  ? '!font-widget-primary !normal-case text-fontSecondary-500'
                  : 'text-fontSecondary-500',
            ]"
          >
            {{ description }}
          </p>
        </div>
      </slot>
      <div class="widget-base-checkbox">
        <fieldset v-if="incidentLocation?.latitude">
          <legend class="sr-only">{{ controlOptions.sameAs || 'Same as incident location' }}</legend>
          <div class="widget-base-checkbox_checkbox">
            <div class="flex h-6 items-center">
              <WInput
                v-model="isSameAsIncidentLocation"
                name="checkboxcontrol"
                :id="control.control.value.id + '-input'"
                type="checkbox"
                :disabled="!control.control.value.enabled"
                :autofocus="control.appliedOptions.value.focus"
                :placeholder="control.appliedOptions.value.placeholder"
                class="widget-base-checkbox_input"
                @update:model-value="setSameAsIncidentLocation"
                @keydown.enter="
                  ((isSameAsIncidentLocation = !isSameAsIncidentLocation),
                  setSameAsIncidentLocation(isSameAsIncidentLocation))
                "
              />
            </div>
            <div
              class="ml-3 cursor-pointer text-sm leading-6"
              @click="
                () => (
                  (isSameAsIncidentLocation = !isSameAsIncidentLocation),
                  setSameAsIncidentLocation(isSameAsIncidentLocation)
                )
              "
            >
              <label for="checkboxcontrol" class="widget-base-checkbox_label cursor-pointer">{{
                controlOptions.sameAs || 'Same as incident location'
              }}</label>
            </div>
          </div>
        </fieldset>
        <GoogleMap
          ref="googleMap"
          v-if="typeof controlOptions === 'object'"
          :model-value="currentLocation"
          :is-open="isMapOpen"
          :main-icon="controlOptions.blank.icon"
          :btn-icon="controlOptions.blank.btnIcon"
          :confirm-text="controlOptions.overlay.confirm"
          :cancel-text="controlOptions.overlay.cancel"
          :cancel-icon="themeIcons.close"
          :title="controlOptions.blank.cta"
          :w3w-label="controlOptions.filled.w3wLabel"
          :location-label="controlOptions.filled.locationLabel"
          :search-icon="controlOptions.overlay.preIcon"
          :marker-icon="controlOptions.overlay.marker"
          @update:model-value="locationChange"
          @update:is-open="toggleMap"
        ></GoogleMap>
      </div>
      <Modal v-if="isOpenDeleteModal" v-model="isOpenDeleteModal" hide-close>
        <template #header
          ><p class="title2-bold">{{ controlOptions.deleteOverlay?.title || 'Delete location?' }}</p>
        </template>
        <div class="flex h-full w-full flex-col items-start gap-4">
          <p>Are you sure you want to delete this location?</p>
          <p>This action can not be undone</p>
        </div>
        <template #footer>
          <div class="w-full">
            <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isOpenDeleteModal = false">
              {{ controlOptions.deleteOverlay?.cancel || 'No, keep location' }}
            </WButton>
            <WButton type="primary" class="widget-confirm-modal_btn" @click="deleteLocation">
              {{ controlOptions.deleteOverlay?.confirm || 'Yes, I am sure' }}
            </WButton>
          </div>
        </template>
      </Modal>
    </section>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { ControlElement, JsonFormsSubStates } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { ref, inject, computed, onMounted } from 'vue';
  import { useWidgetControl } from '../util/composition';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { getClientTheme, themeIcons, checkTheme } from 'src/utils/config';
  import GoogleMap from 'components/base/GoogleMap.vue';
  import WButton from 'components/base/Button.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import Modal from 'src/components/base/ConfirmModal.vue';
  import WInput from 'src/components/base/Input.vue';
  import { ILocationInfo } from '../../../utils/map';

  export type LocationOptions = {
    sameAs: string;
    blank: Blank;
    overlay: Overlay;
    filled: Filled;
    description: string;
    deleteOverlay: DeleteOverlayOptions;
  };

  interface Filled {
    edit: string;
    delete: string;
    locationLabel?: string;
    w3wLabel?: string;
  }

  interface Overlay {
    confirm: string;
    cancel: string;
    preIcon: string;
    clearIcon: string;
    marker: string;
  }
  interface DeleteOverlayOptions {
    confirm: string;
    cancel: string;
    title: string;
  }

  interface Blank {
    icon: string;
    cta: string;
    btnIcon?: string;
  }

  const props = defineProps(rendererProps<ControlElement>());

  const jsonforms = inject<JsonFormsSubStates>('jsonforms');
  const incidentLocation = computed(() => jsonforms?.core?.data?.location as undefined | ILocationInfo);
  const googleMap = ref<{ locationChange: (place: ILocationInfo | undefined) => void }>();
  const isMapOpen = ref(false);
  const isSameAsIncidentLocation = ref(false);
  const isOpenDeleteModal = ref(false);
  const control = useWidgetControl(useJsonFormsControl(props));
  const description = ref(control.control.value.schema.description);
  const controlOptions = ref(
    (control.control.value.schema as unknown as { locationOptions: LocationOptions }).locationOptions
  );

  function isSameLatLng(location: ILocationInfo, toCompare: ILocationInfo) {
    const loc = new google.maps.LatLng(location.latitude, location.longitude);
    const comp = new google.maps.LatLng(toCompare.latitude, toCompare.longitude);
    return loc.equals(comp);
  }

  onMounted(() => {
    if (incidentLocation.value && currentLocation.value) {
      isSameAsIncidentLocation.value = isSameLatLng(incidentLocation.value, currentLocation.value);
    }
  });

  const currentLocation = ref<ILocationInfo | undefined>(control.control.value.data || undefined);

  function toggleMap(value: boolean) {
    isMapOpen.value = value;
    control.setIsFocused(!value);
  }

  function setSameAsIncidentLocation(value: boolean | string) {
    if (value && !!incidentLocation.value?.latitude) {
      googleMap.value?.locationChange(incidentLocation.value);
    } else {
      googleMap.value?.locationChange(undefined);
    }
  }

  function locationChange(location: ILocationInfo | undefined) {
    currentLocation.value = location;
    if (isSameAsIncidentLocation.value && incidentLocation.value && location) {
      isSameAsIncidentLocation.value = isSameLatLng(incidentLocation.value, location);
    }
    control.onChange(location);
  }

  function deleteLocation() {
    currentLocation.value = undefined;
    control.onChange(undefined);
    isSameAsIncidentLocation.value = false;
    isOpenDeleteModal.value = false;
  }
</script>
